// React
import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

// Components
import {
    PrimaryButton,
    DialogBox
} from '../../msreact-kit/packages/html-components';
import Footer from '../BaseLayout/footer';
import ShippingRulesGrid from './shippingRulesGrid';
import Loader from '../Shared/Utils/Loader';
import CustomizedSnackbars from '../Shared/Utils/SnackBar';

// Actions
import {
    getShippingRules,
    deleteShippingRule
} from '../../actions/shipping-action';
import { setSnackbar } from '../../actions/generic-action';

// Constants
import { SHIPPING_RULE_MODE } from './data';

// Css
import './index.scss';

const ShippingManagement = memo((props) => {
    const dispatch = useDispatch();
    const { content, snackbarStatus } = useSelector((state) => ({
        content: state.ContentReducer.content,
        snackbarStatus: state.GenericReducer.snackbarStatus
    }));
    const shippingContent = content?.components?.shipping_management;

    const [shippingRules, setShippingRules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const processedRef = useRef(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    const handlePageChange = (page, size) => {
        if (size === pageSize) {
            setCurrentPage(page);
        } else {
            setPageSize(size);
            setCurrentPage(0);
        }
    };
    
    const loadShippingRules = useCallback(
        async (page, size) => {
            setLoading(true);
            const result = await dispatch(getShippingRules({ page, size }));
            if (result) {
                setShippingRules(result.content ?? []);
                setTotalPages(result.totalPages ?? 0);
            }
            setLoading(false);
        },
        [dispatch]
    );
    
    useEffect(() => {
        loadShippingRules( currentPage, pageSize );
    }, [loadShippingRules, currentPage, pageSize]);
    
    useEffect(() => {
        const state = props.location?.state;
        if (!state || processedRef.current) return;
        processedRef.current = true;
        loadShippingRules(currentPage, pageSize);
        if (state.successMessage) {
            dispatch(
                setSnackbar({
                    showSnackbar: true,
                    message: state.successMessage,
                    severity: 'success'
                })
            );
        }
        props.history.replace('/dashboard/shipping-management', {});
    }, []);

    useEffect(() => {
        return () => {
            dispatch(setSnackbar({ showSnackbar: false, message: '' }));
        };
    }, []);
    
    const handleAction = (type, row) => {
        if (type === 'delete') {
            setPendingDelete(row);
            return;
        }
        props.history.push({
            pathname: '/dashboard/shipping-management/shipping-rule',
            state: { shippingRule: row },
            search: `?mode=${type}`
        });
    };

    const handleDeleteConfirm = async () => {
        const shippingRuleId = pendingDelete?.shippingRuleId;
        setPendingDelete(null);
        if (shippingRuleId) {
            const result = await dispatch(deleteShippingRule(shippingRuleId));
            if (result !== null) {
                dispatch(
                    setSnackbar({
                        showSnackbar: true,
                        message: shippingContent?.shipping_details?.messages?.shipping_rule_deleted,
                        severity: 'success'
                    })
                );
                await loadShippingRules( currentPage, pageSize );
            }
        }
    };
    
    const handleCreateShippingRule = () => {
        props.history.push({
            pathname: '/dashboard/shipping-management/shipping-rule',
            search: `?mode=${SHIPPING_RULE_MODE.add}`
        });
    };

    return (
        <div>
            {loading && <Loader />}

            {snackbarStatus?.showSnackbar && (
                <CustomizedSnackbars
                    severity={snackbarStatus?.severity}
                    message={snackbarStatus?.message}
                    changeState={() =>
                        dispatch(setSnackbar({ showSnackbar: false, message: '' }))
                    }
                />
            )}

            {pendingDelete && (
                <DialogBox
                    title={shippingContent?.delete_dialog?.title}
                    subTitle={`${shippingContent?.delete_dialog?.subtitle}`}
                    isModal
                    isSuccess={false}
                    handleConfirm={handleDeleteConfirm}
                    handleCancel={() => setPendingDelete(null)}
                />
            )}

            <div className='shipping-management__container'>
                <div className='shipping-management__container__title-wrapper'>
                    <div className='shipping-management__container__title'>
                        <h5 className='shipping-management__container__header -size-l'>
                            {shippingContent?.page?.title}
                        </h5>
                        <div className='shipping-management__container__text'>
                            <p>{shippingContent?.page?.subtitle}</p>
                        </div>
                    </div>

                    <div className='shipping-management__grid'>
                        <PrimaryButton
                            id='createShippingRule'
                            label=''
                            onClick={handleCreateShippingRule}
                            className='shipping-management__add-button'
                        >
                            <span className='a-button__label'>
                                <i className='a-icon boschicon-bosch-ic-add' />
                                <span className='d-sm-none'>
                                    {shippingContent?.page?.create_button}
                                </span>
                            </span>
                        </PrimaryButton>
                    </div>
                </div>

                <div className='shipping-management__table'>
                    <ShippingRulesGrid
                        data={shippingRules}
                        onAction={handleAction}
                        tableContent={shippingContent?.table}
                        statusLabels={shippingContent?.shipping_details?.status_labels}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>

            <Footer customClassName='d-lg-none' />
        </div>
    );
});

ShippingManagement.propTypes = {
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired
};

export default ShippingManagement;
